import { Pipe, PipeTransform } from '@angular/core';
import { StoreTimezoneService } from './store-timezone.service';

/**
 * StoreDatePipe
 *
 * Angular pipe for displaying dates in store timezone.
 *
 * Usage:
 * {{ order.submittedAt | storeDate:store.timeZone:'datetime':store.locale }}
 * {{ order.wishTime | storeDate:store.timeZone:'date':store.locale }}
 * {{ opening.startTime | storeDate:store.timeZone:'time':store.locale:store.settings.TIME_FORMAT_SELECTION }}
 * {{ balanceSummary.openingTimestamp | storeDate:store.timeZone:'customWithTimeFormat':store.locale:'EEE dd MMM yyyy - HH:mm:ss':store.settings.TIME_FORMAT_SELECTION }}
 *
 * Format types:
 * - 'datetime': Full date and time
 * - 'date': Date only
 * - 'time': Time only
 * - 'custom': Custom format (provide as 5th parameter) - does not respect time format
 * - 'customWithTimeFormat': Custom format that respects store time format (12h/24h) - provide format as 5th param and time format as 6th
 */
@Pipe({
  name: 'storeDate',
})
export class StoreDatePipe implements PipeTransform {
  constructor(private storeTimezoneService: StoreTimezoneService) {}

  transform(
    value: string | Date,
    storeTimezone: string,
    formatType:
      | 'datetime'
      | 'date'
      | 'time'
      | 'custom'
      | 'customWithTimeFormat' = 'datetime',
    locale?: string,
    customFormatOrTimeFormat?: string,
    timeFormatForCustom?: string,
  ): string {
    if (!value || !storeTimezone) {
      return '';
    }

    try {
      switch (formatType) {
        case 'date':
          return this.storeTimezoneService.formatDateInStoreTimezone(
            value,
            storeTimezone,
            locale,
          );

        case 'time':
          const timeFormat = this.convertTimeFormat(customFormatOrTimeFormat);
          return this.storeTimezoneService.formatTimeInStoreTimezone(
            value,
            storeTimezone,
            timeFormat,
            locale,
          );

        case 'datetime':
          const dateTimeFormat = this.convertTimeFormat(
            customFormatOrTimeFormat,
          );
          return this.storeTimezoneService.formatDateTimeInStoreTimezone(
            value,
            storeTimezone,
            dateTimeFormat,
            locale,
          );

        case 'custom':
          return this.storeTimezoneService.formatInStoreTimezone(
            value,
            storeTimezone,
            customFormatOrTimeFormat,
            locale,
          );

        case 'customWithTimeFormat':
          const timeFormatConverted =
            this.convertTimeFormat(timeFormatForCustom);
          return this.storeTimezoneService.formatInStoreTimezoneWithTimeFormat(
            value,
            storeTimezone,
            customFormatOrTimeFormat,
            timeFormatConverted,
            locale,
          );

        default:
          return this.storeTimezoneService.formatDateTimeInStoreTimezone(
            value,
            storeTimezone,
            '24h',
            locale,
          );
      }
    } catch (error) {
      console.error('Error in StoreDatePipe:', error);
      return '';
    }
  }

  private convertTimeFormat(format?: string): '12h' | '24h' {
    if (!format) {
      return '24h';
    }

    return format === 'TIME_FORMAT_12H' || format === '12h' ? '12h' : '24h';
  }
}
